import React, { createRef, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Container,
  Fade,
  Snackbar,
  SnackbarProps,
  Stack,
  StackProps,
  SxProps,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import moment from 'moment';
import { pdfjs } from 'react-pdf';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { TP_COLORS } from '../../../constants';
import { useAppSelector } from '../../../hooks';
import {
  getClassFromLocalStorage,
  getLeadModalOpenFromLocalStorage,
  getPDFThemeModeFromLocalStorage,
  getPdfTriggerMessageStatusFromLocalStorage,
  getProductValueForSubTopicShowedFromLocalStorage,
  getSlideViewsLimitFromLocalStorage,
  getSubjectColorNew,
  getUrlConvertedString,
  isMobileScreen,
  removeLeadModalOpenFromLocalStorage,
  setPDFThemeModeToLocalStorage,
  setPdfTriggerMessageStatusToLocalStorage,
  setProductValueForSubTopicShowedToLocalStorage,
} from '../../../helper';
import socket from '../../../helper/Socket';

import {
  IGroupedSubTopic,
  ISubTopic,
  getSubTopicById,
  getSubTopicState,
  resetSubTopic,
} from '../../../store/SubTopicSlice';
import {
  getSessionState,
  resetSession,
  selectStudent,
} from '../../../store/SessionSlice';
import {
  createBookmark,
  getBookmark,
  getBookmarkState,
} from '../../../store/BookmarkSlice';
import {
  getAccessLimitState,
  toggleLeadGenModal,
  togglePremiumModal,
} from '../../../store/AccessLimitSlice';
import { getAuthState } from '../../../store/AuthSlice';
import {
  ITopic,
  getTopicState,
  getTopicsWithSubTopics,
} from '../../../store/TopicSlice';
import { saveStudentProgress } from '../../../store/ProgressSlice';
import { saveUserUsage } from '../../../store/AccessLimitSlice/AccessLimitApi';
import { getRandomQuestionsBySubTopic } from '../../../store/QuestionSlice/QuestionApi';
import { getQuestionState } from '../../../store/QuestionSlice';

import SubTopicVideoLibrary from './components/SubTopicVideoLibrary';
import FrequentlyAskedQuestionSection from './components/FrequentlyAskedQuestionSection';
import SubTopicReviewsSection from './components/SubTopicReviewsSection';
import Breadcrumbs, {
  BreadcrumbOption,
} from '../../../components/BreadCrumb/Breadcrumbs';
import SubjectProgressBar from '../Subject/components/SubjectProgressBar';
import BasicLoader from '../../../components/Loaders/BasicLoader';
import Session from './Session';
import PdfViewerHeader from '../../../components/PdfViewer/PdfViewerHeader';
import HomeFooter from '../../Home/components/HomeFooter';
import PdfViewer from '../../../components/PdfViewer/PdfViewer';
import PdfViewerModal from './modals/PdfViewerModal';
import SessionStudents from './components/SessionStudents';
import ExamTips from './components/ExamTips';
import PracticeQuestions from './components/PracticeQuestions';
import ProductValueForSubTopic from '../ProductValue/ProductValueForSubTopic';
import ExploreTopicsBar from './components/ExploreTopicsBar';
import ResourcesSection from './components/ResourcesSection';
import AttemptQuizBar from './components/AttemptQuizBar';

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.js`;

const constructBreadcrumbOptions = (topic?: ITopic, subTopic?: ISubTopic) => {
  const options: Array<BreadcrumbOption> = [
    {
      type: 'link',
      title: 'Home',
      link: `${process.env.PUBLIC_URL}`,
    },
  ];

  if (topic) {
    const curriculum = getUrlConvertedString(topic.curriculum);
    const subject = getUrlConvertedString(topic.subject);
    const subjectLevel = getUrlConvertedString(topic.subjectLevel);
    const subjectCode = getUrlConvertedString(topic.subjectCode);
    const topicName = getUrlConvertedString(topic.name);

    options.push({
      type: 'link',
      title: topic.curriculum,
      link: `${process.env.PUBLIC_URL}/${curriculum}`,
    });

    options.push({
      type: 'link',
      title: topic.subject,
      link: `${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}`,
    });

    options.push({
      type: 'link',
      title: topic.name,
      link: `${process.env.PUBLIC_URL}/dashboard/${curriculum}/${subject}/${subjectLevel}/${subjectCode}/${topicName}/${topic._id}`,
    });
  }

  if (subTopic) {
    options.push({
      type: 'text',
      title: subTopic.name,
      highlight: true,
    });
  }

  return options;
};

export const scales = [0.25, 0.5, 0.75, 1, 1.5, 2, 2.5, 3];

const topStackProps: StackProps['sx'] = {
  direction: 'row',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexWrap: 'wrap',
  style: { padding: '30px 0' },
};

const snackbarProps: SnackbarProps = {
  anchorOrigin: { vertical: 'top', horizontal: 'center' },
  autoHideDuration: 5000,
  message: (
    <span
      style={{
        display: 'inline-block',
        lineHeight: '1.5',
        verticalAlign: 'middle',
        textAlign: 'center',
      }}
    >
      Don&apos;t rush! Each slide builds your knowledge step by step.{' '}
      <img
        src={`${process.env.PUBLIC_URL}/images/resourceLib/turtle.png`}
        alt="icon"
        style={{
          width: '25px',
          height: '15px',
          marginLeft: '5px',
          verticalAlign: 'middle',
        }}
      />
    </span>
  ),
  ContentProps: {
    sx: {
      mt: { xs: '55%', sm: '55%', md: '55%', lg: '25%' },
      position: 'relative',
      left: { lg: '40%' },
      fontSize: '15px',
      color: '#B76E00',
      border: '1px solid  #FFAB00',
      borderRadius: '45px',
      minWidth: { xs: '300px', sm: '340px', md: '360px', lg: '400px' },
      maxWidth: { xs: '300px', sm: '340px', md: '360px', lg: '400px' },
      background: '#FFF5CC',
      boxShadow: 'unset',
      pl: '30px',
      pr: '30px',
      textAlign: 'center',
    },
  },
};

export interface ISessionUser {
  _id: string;
  firstname: string;
  lastname: string;
  username: string;
  autoConnectDisabled?: boolean;
  isOnline: boolean;
}

type TState = {
  themeMode: 'light' | 'dark';
  initialLoading: boolean;
  pageNo: string;
  fileUrl: string;
  totalPages: number;
  scale: number;
  isViewPdfViewerModalOpen: boolean;
  studentId?: string;
};

const initialState: TState = {
  themeMode: getPDFThemeModeFromLocalStorage() || 'light',
  initialLoading: true,
  pageNo: '1',
  fileUrl: '',
  totalPages: 0,
  scale: scales[3],
  isViewPdfViewerModalOpen: false,
};

const Resources: React.FC = () => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const divRef: any = createRef();
  const params = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const theme = useTheme();
  const belowMd = useMediaQuery(theme.breakpoints.down('md'));
  const belowLg = useMediaQuery(theme.breakpoints.down('lg'));

  // State
  const [state, setState] = useState(initialState);
  const [windowSize, setWindowSize] = useState<number | undefined>(undefined);
  const [containerMaxWidth, setContainerMaxWidth] = useState<
    number | undefined
  >(undefined);
  const [relatedStudents, setRelatedStudents] = useState<ISessionUser[]>([]);
  const [isWhiteboardEnable, setIsWhiteboardEnable] = useState(false);
  const [isProductValueForSubTopicOpen, setProductValueForSubTopic] = useState(
    false
  );
  const [skippedSlides, setSkippedSlides] = useState<number>(0);
  const [firstSkipTime, setFirstSkipTime] = useState<number | null>(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [subTopics, setSubTopics] = useState<IGroupedSubTopic[]>([]);

  // Store
  const subTopicState = useAppSelector(getSubTopicState);
  const bookmarkState = useAppSelector(getBookmarkState);
  const sessionState = useAppSelector(getSessionState);
  const authState = useAppSelector(getAuthState);
  const accessLimitState = useAppSelector(getAccessLimitState);
  const questionState = useAppSelector(getQuestionState);
  const topicState = useAppSelector(getTopicState);

  const subTopicId = params.subtopicId || '';
  const subTopicData = subTopicState.subTopic;
  const bookmarkData = bookmarkState.bookmark;
  const authUser = authState.user;
  const { usage, limitation } = accessLimitState;
  const { session } = sessionState;
  const topicData =
    subTopicData && subTopicData.topic ? subTopicData.topic : undefined;
  const userRole = authState.user?.role.name;
  const questionList = questionState.list;
  const { topicsWithSubTopics } = topicState;

  const breadcrumbOptions = constructBreadcrumbOptions(topicData, subTopicData);
  const subjectColor = getSubjectColorNew(topicData?.subject || '');
  const classData = getClassFromLocalStorage();

  const isLoading = state.initialLoading;
  const isPremiumModal =
    authUser &&
    (authUser.role.name === 'Student' || authUser.isLeadGenSubmitted);
  const containerStyles: SxProps = {
    backgroundColor: '#E5F7F666',
    minHeight: { xs: 'calc(100vh - 56px)', sm: 'calc(100vh - 64px)' },
  };

  const selectedSubTopicIndex = subTopics.length
    ? subTopics.findIndex((item) => item._id === subTopicId)
    : -1;

  let themeTextColor = '#1E293B';

  if (state.themeMode === 'dark') {
    themeTextColor = TP_COLORS.white;
    containerStyles.backgroundColor = '#0F172A';
  }

  containerStyles.color = themeTextColor;

  const pdfContainerStyles: React.CSSProperties = {
    maxWidth: containerMaxWidth,
    overflow: 'auto',
    outline: 'none',
    borderRadius: '15px',
  };

  let maxWidthForPdfViewer = containerMaxWidth;
  let subject = '';
  let topicName = '';
  let subTopicName = '';

  if (subTopicData && topicData) {
    subject = topicData.subject || '';
    topicName = topicData.name || '';
    subTopicName = subTopicData.name || '';
  }

  if (containerMaxWidth) {
    if (containerMaxWidth > 900) {
      maxWidthForPdfViewer = 900;
    } else if (containerMaxWidth < 900 && !isMobileScreen()) {
      maxWidthForPdfViewer = containerMaxWidth - 100;
    }
  }

  useEffect(() => {
    if (subTopicId) {
      dispatch(getSubTopicById(subTopicId));
      dispatch(getRandomQuestionsBySubTopic(subTopicId));
    }
  }, [subTopicId]);

  useEffect(() => {
    if (topicData) {
      dispatch(
        getTopicsWithSubTopics({
          curriculum: topicData.curriculum,
          subject: topicData.subject,
          subjectCode: topicData.subjectCode,
          subjectLevel: topicData.subjectLevel,
        })
      );
    }
  }, [topicData]);

  const showedCount = 1;
  const productValueForSubTopicShowed = getProductValueForSubTopicShowedFromLocalStorage();

  useEffect(() => {
    window.addEventListener('resize', handleWindowResize);

    handleWindowResize();
    dispatch(resetSubTopic());
    removeLeadModalOpenFromLocalStorage();

    if (
      !productValueForSubTopicShowed ||
      parseFloat(productValueForSubTopicShowed) < showedCount
    ) {
      setProductValueForSubTopic(true);
    }

    return () => {
      window.removeEventListener('resize', handleWindowResize);
      dispatch(resetSubTopic());
    };
  }, []);

  useEffect(() => {
    resizePdfViewer();

    setTimeout(() => {
      resizePdfViewer();
    }, 500);
  }, [divRef]);

  useEffect(() => {
    if (
      subTopicState.subTopic &&
      subTopicState.subTopic.resources &&
      subTopicState.subTopic.resources.length > 0
    ) {
      const resource = subTopicState.subTopic.resources[0];

      setState((prevState) => ({
        ...prevState,
        initialLoading: false,
        fileUrl: resource.webContentLink || '',
        pageNo:
          subTopicData?._id === bookmarkData.subTopicId
            ? bookmarkData.pageNo
            : '1',
        totalPages: 0,
        scale: scales[3],
        isViewPdfViewerModalOpen: false,
      }));

      if (
        subTopicId &&
        userRole === 'Tutor' &&
        classData &&
        classData.tutorId &&
        classData.studentId &&
        classData.end &&
        classData.meetingId &&
        moment(classData.end).add(30, 'minutes') > moment()
      ) {
        dispatch(
          createBookmark({
            tutorId: classData.tutorId,
            studentId: classData.studentId,
            subject,
            subTopicId,
            subTopicName: subTopicData?.name || '',
            pageNo: '1',
            ...(moment(classData.end) < moment()
              ? { meetingId: classData.meetingId }
              : {}),
          })
        );
      }

      if (
        classData &&
        classData.tutorId &&
        classData.studentId &&
        !bookmarkData.pageNo
      ) {
        dispatch(
          getBookmark({
            tutorId: classData.tutorId,
            studentId: classData.studentId,
            subject: classData.subject,
          })
        );
      }
    }
  }, [subTopicState.subTopic]);

  useEffect(() => {
    if (bookmarkData.pageNo) {
      setState((prevState) => ({
        ...prevState,
        pageNo: bookmarkData.pageNo,
      }));
    }
  }, [bookmarkData.pageNo]);

  useEffect(() => {
    socket.on('receive_students', (data) => {
      if (Array.isArray(data)) {
        setRelatedStudents(data);
      }
    });

    socket.on('disconnect_student', (data) => {
      if (
        data.student &&
        userRole === 'Tutor' &&
        getOnlineStudent(data.student)
      ) {
        setRelatedStudents((prevState) => {
          const studentList = [...prevState];
          const studentIndex = studentList.findIndex(
            (item) => item._id === data.student._id
          );
          if (studentIndex !== -1) {
            studentList[studentIndex].isOnline = false;
          }
          if (studentList.length === 0) {
            dispatch(resetSession());
            dispatch(selectStudent(''));
          }
          return studentList;
        });
      }
    });

    socket.on('connect_student', (data) => {
      if (
        data.student &&
        userRole === 'Tutor' &&
        getOnlineStudent(data.student)
      ) {
        setRelatedStudents((prevState) => {
          const studentList = [...prevState];
          const index = studentList.findIndex(
            (item) => item._id === data.student._id
          );
          if (index !== -1) {
            studentList[index].isOnline = true;
            studentList[index].autoConnectDisabled = true;
          }
          return studentList;
        });
      }
    });
  }, [socket]);

  useEffect(() => {
    if (topicsWithSubTopics.length) {
      const topics = topicsWithSubTopics.filter((item) => !item.isAssessment);

      if (topics.length) {
        const foundTopic = topics.find(
          (topicItem) => getUrlConvertedString(topicItem._id) === topicData?._id
        );

        if (foundTopic) {
          const filteredSubTopics = foundTopic.subTopics.filter((subTopic) => {
            const hasResources = !!(
              subTopic.resources?.length ||
              (((subTopic.resources?.length &&
                subTopic.practiceQuestionsExists) ||
                subTopic.videoLinks?.length) &&
                (subTopic.quizCount ?? 0) > 0)
            );
            return hasResources;
          });

          setSubTopics(filteredSubTopics);
        }
      }
    }
  }, [topicsWithSubTopics, topicData?._id]);

  useEffect(() => {
    if (session) {
      setState((prevState) => ({
        ...prevState,
        pageNo:
          prevState.studentId !== session.student ? '1' : prevState.pageNo,
        studentId: session.student,
      }));
    }
  }, [session]);

  const handleWindowResize = () => {
    const windowInnerWidth = window.innerWidth;

    if (windowSize !== windowInnerWidth) {
      setWindowSize(window.innerWidth);
    }
  };

  const resizePdfViewer = () => {
    const { current } = divRef;

    if (current) {
      const boundingRect = current.getBoundingClientRect();

      if (boundingRect.width !== containerMaxWidth) {
        setContainerMaxWidth(boundingRect.width);
      }
    }
  };

  const togglePdfViewerModal = () => {
    setState((prevState) => ({
      ...prevState,
      isViewPdfViewerModalOpen: !prevState.isViewPdfViewerModalOpen,
    }));
  };

  const onDocumentLoadSuccess = (numPages) => {
    setState((prevState) => ({
      ...prevState,
      initialLoading: false,
      totalPages: numPages,
    }));
  };

  const handleAttemptQuiz = () => {
    if (subTopicData && topicData) {
      const urlConvertedCurriculum = getUrlConvertedString(
        topicData.curriculum
      );

      const urlConvertedSubject = getUrlConvertedString(topicData.subject);
      const urlConvertedTopic = getUrlConvertedString(topicData.name);
      const urlConvertedSubTopic = getUrlConvertedString(subTopicData.name);

      navigate(
        `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlConvertedTopic}/${urlConvertedSubTopic}/${subTopicData._id}/quiz`
      );
    }
  };

  const generateSubTopicUrl = (subTopic) => {
    const urlConvertedSubTopic = getUrlConvertedString(subTopic.name);
    const urlCovertedTopic = getUrlConvertedString(topicData?.name || '');
    const urlConvertedSubject = getUrlConvertedString(topicData?.subject || '');
    const urlConvertedCurriculum = getUrlConvertedString(
      topicData?.curriculum || ''
    );

    return `${process.env.PUBLIC_URL}/dashboard/${urlConvertedCurriculum}/${urlConvertedSubject}/${urlCovertedTopic}/${urlConvertedSubTopic}/${subTopic._id}`;
  };

  const handleNextSubTopic = () => {
    if (subTopics.length && selectedSubTopicIndex < subTopics.length - 1) {
      navigate(generateSubTopicUrl(subTopics[selectedSubTopicIndex + 1]), {
        replace: true,
      });
    }
  };

  const handlePreviousSubTopic = () => {
    if (subTopics.length && selectedSubTopicIndex > 0) {
      navigate(generateSubTopicUrl(subTopics[selectedSubTopicIndex - 1]), {
        replace: true,
      });
    }
  };

  const onChangePage = (pageNo: string) => {
    const isNext = Number(state.pageNo) < Number(pageNo);
    const slideViewsLimit = getSlideViewsLimitFromLocalStorage();
    const leadModalOpen = getLeadModalOpenFromLocalStorage();
    const isSkipped =
      topicName.toLocaleLowerCase().search('overview') < 0 &&
      subTopicName.toLocaleLowerCase().search('overview') < 0 &&
      subTopicName.toLocaleLowerCase().search('planner') < 0 &&
      topicName.toLocaleLowerCase().search('planner') < 0;

    if (
      isSkipped &&
      limitation &&
      slideViewsLimit &&
      Number(slideViewsLimit) > 0 &&
      usage.slideViews + 2 >= Number(slideViewsLimit) &&
      !leadModalOpen
    ) {
      dispatch(isPremiumModal ? togglePremiumModal() : toggleLeadGenModal());
    } else if (!leadModalOpen) {
      setState((prevState) => ({
        ...prevState,
        pageNo,
      }));

      // Save Resource Progress
      if (
        subTopicId &&
        userRole === 'Student' &&
        Number(state.pageNo) < Number(pageNo)
      ) {
        dispatch(
          saveStudentProgress({
            subTopicId,
            resourceCompletion:
              (Number(pageNo) / (state.totalPages + session.questions.length)) *
              100,
          })
        );
      }

      if (
        isNext &&
        isSkipped &&
        authUser &&
        (userRole === 'Guest' || userRole === 'Student')
      ) {
        dispatch(
          saveUserUsage({
            userId: authUser._id,
            option: 'slideViews',
          })
        );
      }

      if (isNext) {
        const now = Date.now();
        const triggerMessageStatuses = getPdfTriggerMessageStatusFromLocalStorage();
        if (!triggerMessageStatuses[subTopicId]) {
          if (firstSkipTime === null) {
            setFirstSkipTime(now);
            setSkippedSlides(1);
          } else {
            const timeDiff = (now - firstSkipTime) / 1000;
            if (timeDiff <= 15) {
              setSkippedSlides((prev) => prev + 1);
              if (skippedSlides + 1 >= 5) {
                setIsPopupOpen(true);
                setSkippedSlides(0);
                setFirstSkipTime(null);
                setPdfTriggerMessageStatusToLocalStorage(subTopicId, true);
              }
            } else {
              setSkippedSlides(1);
              setFirstSkipTime(now);
            }
          }
        }
      }

      if (
        subTopicId &&
        userRole === 'Tutor' &&
        classData &&
        classData.tutorId &&
        classData.studentId &&
        classData.end &&
        classData.meetingId &&
        moment(classData.end).add(30, 'minutes') > moment()
      ) {
        dispatch(
          createBookmark({
            tutorId: classData.tutorId,
            studentId: classData.studentId,
            subject,
            subTopicId,
            subTopicName: subTopicData?.name || '',
            pageNo,
            ...(moment(classData.end) < moment()
              ? { meetingId: classData.meetingId }
              : {}),
          })
        );
      }
    } else {
      setState((prevState) => ({
        ...prevState,
        pageNo,
      }));
    }
  };

  const handleClosePopup = () => {
    setTimeout(() => {
      setIsPopupOpen(false);
      setPdfTriggerMessageStatusToLocalStorage(subTopicId, true);
    }, 5000);
  };

  const handleScaleChange = (type: 'inc' | 'dec') => {
    const _index = scales.findIndex((sc) => sc === state.scale);

    if (
      type === 'inc' &&
      state.scale !== scales[scales.length - 1] &&
      _index > -1
    ) {
      setState((prevState) => ({
        ...prevState,
        scale: scales[_index + 1],
      }));
    } else if (type === 'dec' && state.scale !== scales[0] && _index > -1) {
      setState((prevState) => ({
        ...prevState,
        scale: scales[_index - 1],
      }));
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (
      e.keyCode === 39 &&
      Number(state.pageNo) < state.totalPages + session.questions.length + 1
    ) {
      onChangePage((Number(state.pageNo) + 1).toString());
    } else if (e.keyCode === 37 && Number(state.pageNo) > 1) {
      onChangePage((Number(state.pageNo) - 1).toString());
    }
  };

  const onChangeStudent = (studentId: string) => {
    if (studentId) {
      socket.emit('select_student', { studentId, subTopicId });
    } else {
      dispatch(resetSession());
    }
  };

  const getOnlineStudent = (student: ISessionUser) => {
    const studentsData = authState.user?.students || [];
    return studentsData.find((item) => item.username === student.username);
  };

  const toggleTheme = () => {
    const newTheme = state.themeMode === 'light' ? 'dark' : 'light';
    setState((prevState) => ({
      ...prevState,
      themeMode: newTheme,
    }));
    setPDFThemeModeToLocalStorage(newTheme);
  };

  const checkIsResource = () => {
    if (
      subTopicData &&
      subTopicData.topic &&
      subTopicData.name.toLocaleLowerCase().search('worksheet') < 0 &&
      subTopicData.name.toLocaleLowerCase().search('overview') < 0 &&
      subTopicData.name.toLocaleLowerCase().search('topic planner') < 0 &&
      subTopicData.topic.name.toLocaleLowerCase().search('overview') < 0
    ) {
      return true;
    }
    return false;
  };
  const isResource = checkIsResource();

  return (
    <Box ref={divRef} sx={containerStyles}>
      <Container maxWidth="lg">
        <Stack {...topStackProps}>
          <Breadcrumbs
            options={breadcrumbOptions}
            showDropDown={belowMd}
            textColor={
              state.themeMode === 'light' ? TP_COLORS.gray.light : undefined
            }
            highlightColor={subjectColor}
          />
          {userRole === 'Student' && <SubjectProgressBar />}
          {userRole === 'Tutor' && !isLoading && (
            <SessionStudents
              {...{ relatedStudents, onChangeStudent, subjectColor }}
            />
          )}
        </Stack>
      </Container>
      {subTopicState.status === 'loading' && !subTopicState.subTopic ? (
        <div
          style={{
            minHeight: 'calc(100vh - 204px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <BasicLoader />
        </div>
      ) : (
        <Fade in timeout={600}>
          <div>
            <Container maxWidth="lg">
              <Stack
                display="flex"
                direction={belowLg ? 'column' : 'row'}
                width="100%"
                spacing={1}
              >
                {subTopicData && subTopics && (
                  <Box
                    sx={{
                      position: 'sticky',
                      top: !belowLg ? '80px' : null,
                      height: 'fit-content',
                      alignSelf: 'flex-start',
                      zIndex: 2,
                      minWidth: belowLg ? '100%' : '27%',
                      maxWidth: belowLg ? '100%' : '27%',
                    }}
                  >
                    <ResourcesSection
                      subTopicData={subTopicData}
                      subjectColor={subjectColor}
                      questionList={questionList}
                      handleButtonClick={handleAttemptQuiz}
                      handleNextSubTopic={handleNextSubTopic}
                      handlePreviousSubTopic={handlePreviousSubTopic}
                      selectedSubTopicIndex={selectedSubTopicIndex}
                      subTopics={subTopics}
                    />
                  </Box>
                )}
                <Box flexGrow={1} pl={1}>
                  <Stack width="100%">
                    <>
                      {!isLoading && state.fileUrl && (
                        <>
                          {subTopicId && <Session subTopicId={subTopicId} />}
                          <Box
                            tabIndex={0}
                            onKeyDown={onKeyDown}
                            style={pdfContainerStyles}
                            id="resources"
                          >
                            <div
                              style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                minHeight: maxWidthForPdfViewer
                                  ? maxWidthForPdfViewer / 2
                                  : 300,
                              }}
                            >
                              <PdfViewer
                                fileUrl={state.fileUrl}
                                scale={state.scale}
                                selectedPageNumber={state.pageNo}
                                onLoadSuccess={(numPages) =>
                                  onDocumentLoadSuccess(numPages)
                                }
                                pageWidth={
                                  maxWidthForPdfViewer
                                    ? maxWidthForPdfViewer - 80
                                    : maxWidthForPdfViewer
                                }
                                session={session}
                                onChangePage={onChangePage}
                                isWhiteboardEnable={isWhiteboardEnable}
                                isResource={isResource}
                              />

                              <PdfViewerHeader
                                onChangePage={onChangePage}
                                totalPages={
                                  state.totalPages + session.questions.length
                                }
                                pageNo={state.pageNo}
                                scale={state.scale}
                                onScaleChange={handleScaleChange}
                                onFullScreenToggle={togglePdfViewerModal}
                                fileUrl={state.fileUrl}
                                toggleTheme={toggleTheme}
                                maxWidth={
                                  maxWidthForPdfViewer
                                    ? `${maxWidthForPdfViewer - 60}px`
                                    : `${maxWidthForPdfViewer}px`
                                }
                                setIsWhiteboardEnable={setIsWhiteboardEnable}
                                isResource={isResource}
                              />
                              <Snackbar
                                {...snackbarProps}
                                open={isPopupOpen}
                                onClose={handleClosePopup}
                              />
                            </div>
                          </Box>
                        </>
                      )}
                    </>
                    {belowLg && (
                      <AttemptQuizBar handleButtonClick={handleAttemptQuiz} />
                    )}
                    {topicData && topicData.name === 'Overview' && (
                      <ExploreTopicsBar topicData={topicData} />
                    )}
                    <div style={{ padding: '30px 0' }}>
                      {subTopicData ? (
                        <>
                          {/* Exam Tips Section */}
                          {subTopicData.contents &&
                          subTopicData.contents.length ? (
                            <div id="exam-tips" style={{ marginTop: '20px' }}>
                              <ExamTips subTopicData={subTopicData} />
                            </div>
                          ) : null}

                          {/* Videos Library */}
                          {subTopicData.videoLinks &&
                          subTopicData.videoLinks.length ? (
                            <div
                              id="videos-library"
                              style={{ marginBottom: '30px' }}
                            >
                              <Accordion
                                style={{
                                  boxShadow: 'none',
                                  borderRadius: '15px',
                                  border: '1px solid #CBD5E1',
                                }}
                              >
                                <AccordionSummary
                                  expandIcon={<ExpandMoreIcon />}
                                  aria-controls="panel1-content"
                                  id="panel1-header"
                                  style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                  }}
                                >
                                  <img
                                    src={`${process.env.PUBLIC_URL}/images/subtopicPage/video.png`}
                                    alt="video-logo"
                                    style={{
                                      width: '80px',
                                      height: '80px',
                                      marginRight: '15px',
                                    }}
                                  />
                                  <Typography
                                    component="span"
                                    fontSize="25px"
                                    color="#006C9C"
                                    alignContent="center"
                                  >
                                    Watch Related Videos
                                  </Typography>
                                </AccordionSummary>
                                <AccordionDetails>
                                  <SubTopicVideoLibrary
                                    subTopic={subTopicData}
                                    themeMode={state.themeMode}
                                  />
                                </AccordionDetails>
                              </Accordion>
                            </div>
                          ) : null}

                          {/* Practice Questions */}
                          {questionList.length &&
                          subTopicData.resources?.length ? (
                            <div id="practice-questions">
                              <PracticeQuestions subTopicData={subTopicData} />
                            </div>
                          ) : null}
                        </>
                      ) : null}
                    </div>
                  </Stack>
                </Box>
              </Stack>
            </Container>
            {subTopicData ? (
              <>
                {/* FAQs Section */}
                {subTopicData.FAQs && subTopicData.FAQs.length ? (
                  <Container maxWidth="lg">
                    <FrequentlyAskedQuestionSection subTopic={subTopicData} />
                  </Container>
                ) : null}
                <div style={{ height: '60px' }} />
                {subTopicData.reviews && subTopicData.reviews.length ? (
                  <>
                    <Container maxWidth="lg">
                      <SubTopicReviewsSection subTopic={subTopicData} />
                    </Container>
                    <div
                      style={{
                        backgroundColor: '#01A79F',
                        height: '200px',
                        marginTop: '-150px',
                      }}
                    />
                  </>
                ) : null}
              </>
            ) : null}
          </div>
        </Fade>
      )}
      <HomeFooter />
      {/* Modals */}
      <PdfViewerModal
        open={state.isViewPdfViewerModalOpen}
        fileUrl={state.fileUrl}
        pageNumber={state.pageNo}
        onClose={togglePdfViewerModal}
        onChangePageModal={onChangePage}
        toggleTheme={toggleTheme}
        isResource={isResource}
      />
      <ProductValueForSubTopic
        open={isProductValueForSubTopicOpen}
        onClose={() => {
          setProductValueForSubTopic(false);
          setProductValueForSubTopicShowedToLocalStorage(
            showedCount.toString()
          );
        }}
      />
    </Box>
  );
};

export default Resources;
